// Shared data types for follow-up reports and open inspections tracker

export type ObservationStatus = 'Open' | 'In Progress' | 'Closed';

export interface ObservationItem {
  id: string;
  inspectionId: string;
  ref: string;
  category: string;
  description: string;
  severity: 'Critical' | 'Severe' | 'Moderate' | 'Minor' | 'Normal';
  lsdScore: number;
  photoEvidence: string[]; // URLs or base64 from inspector
  correctiveAction: string; // filled by owner
  ownerEvidence: string[]; // URLs or base64 uploaded by owner
  status: ObservationStatus;
  dueDate: string;
  closedDate?: string;
  ownerComment: string;
}

export interface FollowUpReport {
  id: string;
  inspectionId: string;
  vesselName: string;
  imoNumber: string;
  owner: string;
  manager: string;
  inspectionDate: string;
  reportGeneratedDate: string;
  totalObservations: number;
  openCount: number;
  inProgressCount: number;
  closedCount: number;
  observations: ObservationItem[];
  lastOwnerResponseDate?: string;
}

// Mock follow-up report data
export const MOCK_FOLLOW_UP_REPORTS: FollowUpReport[] = [
  {
    id: 'fur-001',
    inspectionId: 'insp-001',
    vesselName: 'CAPE SHANGRILA',
    imoNumber: '9511454',
    owner: 'Pacific Bulk Carriers',
    manager: 'Anglo Eastern Ship Mgmt',
    inspectionDate: '2026-03-28',
    reportGeneratedDate: '2026-03-30',
    totalObservations: 6,
    openCount: 1,
    inProgressCount: 2,
    closedCount: 3,
    lastOwnerResponseDate: '2026-04-10',
    observations: [
      {
        id: 'obs-001-01',
        inspectionId: 'insp-001',
        ref: 'CC.04',
        category: 'CC',
        description: 'Two outstanding conditions of class noted — main engine bearing survey overdue by 3 months.',
        severity: 'Critical',
        lsdScore: 5,
        photoEvidence: [],
        correctiveAction: 'Bearing survey scheduled with ClassNK for next port call at Singapore.',
        ownerEvidence: [],
        status: 'In Progress',
        dueDate: '2026-05-15',
        ownerComment: 'Survey appointment confirmed for 2026-05-10.',
      },
      {
        id: 'obs-001-02',
        inspectionId: 'insp-001',
        ref: 'HS.01',
        category: 'HS',
        description: 'Significant corrosion noted on port side hull plating at frame 42-48, coating breakdown.',
        severity: 'Severe',
        lsdScore: 4,
        photoEvidence: [],
        correctiveAction: 'Hull coating repair completed during Singapore dry dock.',
        ownerEvidence: [],
        status: 'Closed',
        dueDate: '2026-04-30',
        closedDate: '2026-04-12',
        ownerComment: 'Repair completed and verified by class surveyor.',
      },
      {
        id: 'obs-001-03',
        inspectionId: 'insp-001',
        ref: 'MD.05',
        category: 'MD',
        description: 'Three mooring lines showing excessive wear — tails require replacement.',
        severity: 'Severe',
        lsdScore: 4,
        photoEvidence: [],
        correctiveAction: 'New mooring tails ordered and delivered to vessel.',
        ownerEvidence: [],
        status: 'Closed',
        dueDate: '2026-04-15',
        closedDate: '2026-04-08',
        ownerComment: 'All three tails replaced. Photos attached.',
      },
      {
        id: 'obs-001-04',
        inspectionId: 'insp-001',
        ref: 'ER.20',
        category: 'ER',
        description: 'Minor fuel oil leak from auxiliary engine No.2 fuel injection pump.',
        severity: 'Moderate',
        lsdScore: 3,
        photoEvidence: [],
        correctiveAction: 'Injection pump seals replaced by ship\'s engineers.',
        ownerEvidence: [],
        status: 'Closed',
        dueDate: '2026-04-10',
        closedDate: '2026-04-05',
        ownerComment: 'Leak resolved. Engine running normally.',
      },
      {
        id: 'obs-001-05',
        inspectionId: 'insp-001',
        ref: 'BN.03',
        category: 'BN',
        description: 'ECDIS chart update overdue — last update 4 months ago.',
        severity: 'Moderate',
        lsdScore: 3,
        photoEvidence: [],
        correctiveAction: 'ECDIS chart update in progress via satellite connection.',
        ownerEvidence: [],
        status: 'In Progress',
        dueDate: '2026-04-20',
        ownerComment: 'Update 80% complete, expected to finish by 2026-04-18.',
      },
      {
        id: 'obs-001-06',
        inspectionId: 'insp-001',
        ref: 'FF.03',
        category: 'FF',
        description: 'Two portable fire extinguishers in engine room expired — require servicing.',
        severity: 'Minor',
        lsdScore: 2,
        photoEvidence: [],
        correctiveAction: '',
        ownerEvidence: [],
        status: 'Open',
        dueDate: '2026-04-25',
        ownerComment: '',
      },
    ],
  },
  {
    id: 'fur-002',
    inspectionId: 'insp-002',
    vesselName: 'IRON EXPLORER',
    imoNumber: '9634821',
    owner: 'Pacific Bulk Carriers',
    manager: 'V.Ships',
    inspectionDate: '2026-04-02',
    reportGeneratedDate: '2026-04-04',
    totalObservations: 8,
    openCount: 4,
    inProgressCount: 3,
    closedCount: 1,
    observations: [
      {
        id: 'obs-002-01',
        inspectionId: 'insp-002',
        ref: 'ER.25',
        category: 'ER',
        description: 'Emergency generator failed to start on first attempt during drill — battery condition poor.',
        severity: 'Critical',
        lsdScore: 5,
        photoEvidence: [],
        correctiveAction: '',
        ownerEvidence: [],
        status: 'Open',
        dueDate: '2026-04-20',
        ownerComment: '',
      },
      {
        id: 'obs-002-02',
        inspectionId: 'insp-002',
        ref: 'LS.04',
        category: 'LS',
        description: 'Lifeboat No.1 release mechanism requires servicing — annual service overdue.',
        severity: 'Critical',
        lsdScore: 5,
        photoEvidence: [],
        correctiveAction: 'Service engineer booked for Port Hedland.',
        ownerEvidence: [],
        status: 'In Progress',
        dueDate: '2026-04-25',
        ownerComment: 'Service confirmed for 2026-04-22.',
      },
      {
        id: 'obs-002-03',
        inspectionId: 'insp-002',
        ref: 'BT.03',
        category: 'BT',
        description: 'BWTS UV lamp unit showing fault alarm — system operating in bypass mode.',
        severity: 'Severe',
        lsdScore: 4,
        photoEvidence: [],
        correctiveAction: '',
        ownerEvidence: [],
        status: 'Open',
        dueDate: '2026-05-01',
        ownerComment: '',
      },
      {
        id: 'obs-002-04',
        inspectionId: 'insp-002',
        ref: 'MD.03',
        category: 'MD',
        description: 'Mooring winch brake lining on No.3 winch worn beyond acceptable limits.',
        severity: 'Severe',
        lsdScore: 4,
        photoEvidence: [],
        correctiveAction: 'Replacement brake linings ordered — ETA 2 weeks.',
        ownerEvidence: [],
        status: 'In Progress',
        dueDate: '2026-05-05',
        ownerComment: 'Parts on order, tracking number provided.',
      },
      {
        id: 'obs-002-05',
        inspectionId: 'insp-002',
        ref: 'AC.02',
        category: 'AC',
        description: 'Galley exhaust fan not operational — fire risk.',
        severity: 'Severe',
        lsdScore: 4,
        photoEvidence: [],
        correctiveAction: 'Fan motor replaced by ship\'s electrician.',
        ownerEvidence: [],
        status: 'Closed',
        dueDate: '2026-04-10',
        closedDate: '2026-04-09',
        ownerComment: 'Fan operational. Test run completed.',
      },
      {
        id: 'obs-002-06',
        inspectionId: 'insp-002',
        ref: 'PC.02',
        category: 'PC',
        description: 'Oil record book entries incomplete for last 3 ballast water exchanges.',
        severity: 'Moderate',
        lsdScore: 3,
        photoEvidence: [],
        correctiveAction: '',
        ownerEvidence: [],
        status: 'Open',
        dueDate: '2026-04-18',
        ownerComment: '',
      },
      {
        id: 'obs-002-07',
        inspectionId: 'insp-002',
        ref: 'SW.05',
        category: 'SW',
        description: 'Permit to work system not consistently applied for hot work operations.',
        severity: 'Moderate',
        lsdScore: 3,
        photoEvidence: [],
        correctiveAction: 'Master issued standing order and crew briefing conducted.',
        ownerEvidence: [],
        status: 'In Progress',
        dueDate: '2026-04-20',
        ownerComment: 'Toolbox talk records attached.',
      },
      {
        id: 'obs-002-08',
        inspectionId: 'insp-002',
        ref: 'WD.03',
        category: 'WD',
        description: 'Handrail section on port side main deck loose — safety hazard.',
        severity: 'Minor',
        lsdScore: 2,
        photoEvidence: [],
        correctiveAction: '',
        ownerEvidence: [],
        status: 'Open',
        dueDate: '2026-04-22',
        ownerComment: '',
      },
    ],
  },
  {
    id: 'fur-003',
    inspectionId: 'insp-005',
    vesselName: 'MINERAL YANG ZI',
    imoNumber: '9723481',
    owner: 'Minerals Shipping Co.',
    manager: 'Wallem Shipmanagement',
    inspectionDate: '2026-04-10',
    reportGeneratedDate: '2026-04-12',
    totalObservations: 5,
    openCount: 3,
    inProgressCount: 1,
    closedCount: 1,
    observations: [
      {
        id: 'obs-003-01',
        inspectionId: 'insp-005',
        ref: 'ER.06',
        category: 'ER',
        description: 'Main engine performance below acceptable parameters — fuel consumption 8% above baseline.',
        severity: 'Critical',
        lsdScore: 5,
        photoEvidence: [],
        correctiveAction: '',
        ownerEvidence: [],
        status: 'Open',
        dueDate: '2026-05-10',
        ownerComment: '',
      },
      {
        id: 'obs-003-02',
        inspectionId: 'insp-005',
        ref: 'BN.05',
        category: 'BN',
        description: 'VDR annual performance test overdue by 2 months.',
        severity: 'Severe',
        lsdScore: 4,
        photoEvidence: [],
        correctiveAction: 'VDR service engineer scheduled for Zhoushan.',
        ownerEvidence: [],
        status: 'In Progress',
        dueDate: '2026-04-30',
        ownerComment: 'Service booked for 2026-04-28.',
      },
      {
        id: 'obs-003-03',
        inspectionId: 'insp-005',
        ref: 'CC.02',
        category: 'CC',
        description: 'Load line certificate expires in 30 days — renewal application not yet submitted.',
        severity: 'Severe',
        lsdScore: 4,
        photoEvidence: [],
        correctiveAction: 'Renewal application submitted to ClassNK.',
        ownerEvidence: [],
        status: 'Closed',
        dueDate: '2026-04-20',
        closedDate: '2026-04-15',
        ownerComment: 'Certificate renewed. Copy onboard.',
      },
      {
        id: 'obs-003-04',
        inspectionId: 'insp-005',
        ref: 'HS.04',
        category: 'HS',
        description: 'Hatch cover No.4 rubber seal deteriorated — watertight integrity compromised.',
        severity: 'Severe',
        lsdScore: 4,
        photoEvidence: [],
        correctiveAction: '',
        ownerEvidence: [],
        status: 'Open',
        dueDate: '2026-05-01',
        ownerComment: '',
      },
      {
        id: 'obs-003-05',
        inspectionId: 'insp-005',
        ref: 'AC.05',
        category: 'AC',
        description: 'Medical locker missing several required medications per MLC 2006.',
        severity: 'Moderate',
        lsdScore: 3,
        photoEvidence: [],
        correctiveAction: '',
        ownerEvidence: [],
        status: 'Open',
        dueDate: '2026-04-25',
        ownerComment: '',
      },
    ],
  },
  {
    id: 'fur-004',
    inspectionId: 'insp-008',
    vesselName: 'CAPE ALBATROS',
    imoNumber: '9689234',
    owner: 'Cargill Ocean Transportation',
    manager: 'Fleet Management Ltd',
    inspectionDate: '2026-04-15',
    reportGeneratedDate: '2026-04-17',
    totalObservations: 9,
    openCount: 6,
    inProgressCount: 2,
    closedCount: 1,
    observations: [
      {
        id: 'obs-004-01',
        inspectionId: 'insp-008',
        ref: 'ER.15',
        category: 'ER',
        description: 'Steering gear hydraulic pump No.2 inoperative — vessel operating on single pump.',
        severity: 'Critical',
        lsdScore: 5,
        photoEvidence: [],
        correctiveAction: '',
        ownerEvidence: [],
        status: 'Open',
        dueDate: '2026-04-22',
        ownerComment: '',
      },
      {
        id: 'obs-004-02',
        inspectionId: 'insp-008',
        ref: 'FF.01',
        category: 'FF',
        description: 'Fixed CO2 system pressure gauge reading low — system may be partially discharged.',
        severity: 'Critical',
        lsdScore: 5,
        photoEvidence: [],
        correctiveAction: 'CO2 service company contacted.',
        ownerEvidence: [],
        status: 'In Progress',
        dueDate: '2026-04-20',
        ownerComment: 'Service engineer attending vessel at Richards Bay.',
      },
      {
        id: 'obs-004-03',
        inspectionId: 'insp-008',
        ref: 'LS.02',
        category: 'LS',
        description: 'Immersion suits for 3 crew members beyond 5-year replacement date.',
        severity: 'Severe',
        lsdScore: 4,
        photoEvidence: [],
        correctiveAction: '',
        ownerEvidence: [],
        status: 'Open',
        dueDate: '2026-04-25',
        ownerComment: '',
      },
      {
        id: 'obs-004-04',
        inspectionId: 'insp-008',
        ref: 'BN.07',
        category: 'BN',
        description: 'GMDSS DSC controller battery backup failed self-test.',
        severity: 'Severe',
        lsdScore: 4,
        photoEvidence: [],
        correctiveAction: 'Battery replacement ordered.',
        ownerEvidence: [],
        status: 'In Progress',
        dueDate: '2026-04-28',
        ownerComment: 'Parts ordered, ETA 5 days.',
      },
      {
        id: 'obs-004-05',
        inspectionId: 'insp-008',
        ref: 'MD.01',
        category: 'MD',
        description: 'Forecastle deck plating showing significant corrosion — requires renewal.',
        severity: 'Severe',
        lsdScore: 4,
        photoEvidence: [],
        correctiveAction: '',
        ownerEvidence: [],
        status: 'Open',
        dueDate: '2026-05-15',
        ownerComment: '',
      },
      {
        id: 'obs-004-06',
        inspectionId: 'insp-008',
        ref: 'ER.03',
        category: 'ER',
        description: 'Engine room bilge level high — bilge pump operational but source of ingress unidentified.',
        severity: 'Moderate',
        lsdScore: 3,
        photoEvidence: [],
        correctiveAction: '',
        ownerEvidence: [],
        status: 'Open',
        dueDate: '2026-04-22',
        ownerComment: '',
      },
      {
        id: 'obs-004-07',
        inspectionId: 'insp-008',
        ref: 'SW.02',
        category: 'SW',
        description: 'Risk assessment for confined space entry not completed before recent tank inspection.',
        severity: 'Moderate',
        lsdScore: 3,
        photoEvidence: [],
        correctiveAction: 'SMS procedure review conducted with crew.',
        ownerEvidence: [],
        status: 'Closed',
        dueDate: '2026-04-20',
        closedDate: '2026-04-18',
        ownerComment: 'Toolbox talk completed. Records available.',
      },
      {
        id: 'obs-004-08',
        inspectionId: 'insp-008',
        ref: 'AC.04',
        category: 'AC',
        description: 'Crew sanitation — two showers in crew accommodation not functioning.',
        severity: 'Minor',
        lsdScore: 2,
        photoEvidence: [],
        correctiveAction: '',
        ownerEvidence: [],
        status: 'Open',
        dueDate: '2026-04-25',
        ownerComment: '',
      },
      {
        id: 'obs-004-09',
        inspectionId: 'insp-008',
        ref: 'WD.02',
        category: 'WD',
        description: 'Three deck lights on main deck not operational.',
        severity: 'Minor',
        lsdScore: 2,
        photoEvidence: [],
        correctiveAction: '',
        ownerEvidence: [],
        status: 'Open',
        dueDate: '2026-04-22',
        ownerComment: '',
      },
    ],
  },
];
